/**
 * UserFetcher
 *
 * @author Dec Norton <decnorton@gmail.com>
 */

import { Team } from '@app/modules/teams';
import has from 'lodash-es/has';
import { _buildHeaders, Fetcher } from '../../fetchers/fetcher';

export class UserFetcher extends Fetcher {
    get modelName() {
        return 'User';
    }

    get resourceName() {
        return 'users';
    }

    init() {
        this._registerEventType('roles_update');
    }

    current(params = {}) {
        return this._get(this._getUrl(`current`), {
            include: ['roles', 'teams:fields(simple)', 'supports_password_prompts'],
            ...params,
        });
    }

    updateSelectedTeams(teams: Pick<Team, 'id'>[], params: any = {}): angular.IPromise<{ data: Team[] }> {
        return this._post(
            this._getUrl('current/team'),
            { teams: { data: teams }, },
            params
        ).then(response => {
            this.clearCache();

            return response;
        });
    }

    /**
     * @param {?string} teamId
     * @param {Object} params
     */
    getSelectedTeams(params = {}) {
        return this._get(this._getUrl('current/team'), params);
    }

    addRole(userId, roleId, params = {}) {
        const url = this._getUrl(`${userId}/roles/${roleId}`);

        return this._post(url, null, params).then(
            response => {
                this.clearCache();

                this._broadcast('roles_update', response);

                return response;
            },
            error => {
                this._broadcastError('roles_update', error);

                return this.$q.reject(error);
            },
        );
    }

    updateRoles(userId, roles, params = {}) {
        const url = this._getUrl(`${userId}/roles`);

        return this._put(url, { roles }, params).then(
            data => {
                this.clearCache();

                this._broadcast('roles_update', data);

                return data;
            },
            error => {
                this._broadcastError('roles_update', error);

                return this.$q.reject(error);
            },
        );
    }

    /**
     * @param {string} userId
     * @param {Team[]} teams An array of objects, each containing an `id` property with the team id.
     * @param {object} params
     * @returns {Promise}
     */
    setTeams(userId, teams, params = {}) {
        if (!Array.isArray(teams)) {
            return this.$q.reject('`teams` parameter is not an Array');
        }

        for (let i = 0, length = teams.length; i < length; i++) {
            if (!has(teams[i], 'id')) {
                return this.$q.reject('Each team should have an `id` property');
            }
        }

        // Filter out any unnecessary data.
        const _teams = teams.map(team => ({
            id: team.id,
        }));

        return this._post(
            `${this._getUrl(userId)}/teams`,
            {
                teams: {
                    data: _teams,
                },
            },
            params,
        );
    }

    delete(key, body, params = {}) {
        if (!key) {
            return this.$q.reject('Missing key');
        }

        const url = this._getUrl(key);

        this._transformOptions(params);

        return this.$http
            .post(
                url,
                {
                    ...body,
                    _method: 'delete',
                },
                {
                    cache: false,
                    headers: _buildHeaders(),
                    params,
                },
            )
            .then(
                response => {
                    this.clearCache();

                    this._broadcast('delete', response.data);

                    return response.data;
                },
                error => {
                    this.$log.error('Request failed', url, error.status, error.data);

                    this._broadcastError('delete', error);

                    return this._transformError(error);
                },
            );
    }

    logoutSessions(userId, body = {}) {
        const url = this._getUrl(`${userId}/sessions/logout`);

        return this._post(url, body);
    }
}

export function userFetcherProvider() {
    this.$get = instantiateFetcher;

    instantiateFetcher.$inject = ['$injector'];

    function instantiateFetcher($injector) {
        return $injector.instantiate(UserFetcher);
    }
}
